/**
 * Zestaw wybrany w asystencie, przenoszony między ekranami ścieżki wykonawcy.
 *
 * DLACZEGO sessionStorage, a nie tylko stan routera: `navigate(..., { state })`
 * gubi dane przy odświeżeniu strony i przy wejściu z zakładki. Ktoś, kto zatrzyma
 * się na ekranie potwierdzenia i wróci po godzinie, straciłby wybór i musiał
 * przechodzić dobór od nowa. Sesja (a nie localStorage) — bo to wybór na teraz,
 * a nie preferencja do zapamiętania na stałe.
 */

import { kluczKraju } from '../kraj'

const KLUCZ = kluczKraju('vv:wybrany-zestaw')
const KLUCZ_POWROTU = kluczKraju('vv:wybrany-zestaw-powrot')
export const ADRES_DOBORU = '/dobor-zestawu-pv'

/**
 * `opcje.powrot`: adres doboru, z którego wybrano zestaw (prosty dobór wraca do swoich pytań z odpowiedziami).
 * Podany tylko przy wyborze z karty wariantu; bez `opcje` zapamiętany adres zostaje.
 */
export const zapiszZestaw = (zestaw, opcje) => {
  try {
    sessionStorage.setItem(KLUCZ, JSON.stringify(zestaw))
    if (opcje) {
      if (opcje.powrot) sessionStorage.setItem(KLUCZ_POWROTU, opcje.powrot)
      else sessionStorage.removeItem(KLUCZ_POWROTU)
    }
  } catch {
    // Prywatne okno albo pełny magazyn — ścieżka ma działać dalej na stanie routera.
  }
}

export const adresPowrotuDoDoboru = () => {
  try { return sessionStorage.getItem(KLUCZ_POWROTU) || ADRES_DOBORU } catch { return ADRES_DOBORU }
}

export const odczytajZestaw = () => {
  try {
    const surowe = sessionStorage.getItem(KLUCZ)
    return surowe ? JSON.parse(surowe) : null
  } catch {
    return null
  }
}

export const wyczyscZestaw = () => {
  try { sessionStorage.removeItem(KLUCZ) } catch { /* nic nie szkodzi */ }
}

/**
 * Producenci marek z katalogu VoltValue.
 *
 * DLACZEGO ROZPOZNAJEMY PO NAZWIE, A NIE Z POLA: pozycja zestawu zwracana przez
 * silnik doboru nie zawiera producenta — ma `model_name`, `spec`, `seller`,
 * ale nie `manufacturer` (sprawdzone na realnej odpowiedzi). Do czasu, aż backend
 * zacznie go zwracać, wyciągamy markę z nazwy modelu.
 *
 * Lista pochodzi z katalogu (`/api/*_models`), a nie z głowy — inaczej rozpoznanie
 * milczkiem gubiłoby połowę marek. Dłuższe nazwy stoją przed krótszymi, żeby
 * „JA Solar" nie został rozpoznany jako „Solarwatt" i odwrotnie.
 */
const MARKI = [
  'AIKO', 'AVIA Solar', 'Astronergy', 'Bauer', 'Bluesun', 'Canadian Solar', 'DMEGC',
  'Hanersun', 'HT-SAAE', 'HY Solar', 'Hyundai', 'IBC', 'JA Solar', 'Jinko Solar',
  'Jolywood', 'LONGi Solar', 'Ledvance', 'Phono Solar', 'Q-SUN', 'QCells', 'Risen',
  'Selfa', 'SoliTek', 'SunLink', 'Sunova', 'Suntech', 'TCL', 'Tongwei', 'Trina Solar',
  'APsystems', 'Deye', 'Encor', 'FoxESS', 'Fronius', 'GoodWe', 'Growatt', 'Hoymiles',
  'Huawei', 'Hypontech', 'KACO', 'KSTAR', 'Kehua Tech', 'SMA', 'Sofar', 'SolarEdge',
  'Solax', 'Solis', 'Solplanet', 'Sungrow', 'Tigo', 'Victron Energy', 'GOKIN',
  'Anker', 'BYD', 'Breeze Energies', 'Dyness', 'Enphase', 'GSL Energy', 'Heckman',
  'Hinen', 'LG', 'Miellec', 'Pylontech', 'RCT POWER', 'Sunwoda', 'WHES',
].sort((a, b) => b.length - a.length)

const markaZNazwy = (nazwa) => {
  if (!nazwa) return null
  const tekst = nazwa.toLowerCase()
  return MARKI.find((m) => tekst.includes(m.toLowerCase())) || null
}

/** Producenci sprzętu w zestawie — po nich dobieramy wykonawców znających te modele. */
export const producenciZestawu = (zestaw) => {
  const z = (zestaw?.items || [])
    .map((i) => i.manufacturer || i.producent || markaZNazwy(i.model_name))
    .filter(Boolean)
  return [...new Set(z)]
}

export const mocZestawuKwp = (zestaw) => zestaw?.summary?.panels_total_kwp ?? null
